/**
 * Allowlist-based redirect validation. The login and OAuth-callback flows both
 * accept a `redirect` query / sessionStorage value and navigate to it after a
 * successful sign-in. Without an allowlist, an attacker can craft
 * `https://bill.co.za/login?redirect=https://evil.com` to land victims on a
 * phishing page immediately after a real sign-in event.
 *
 * The allowlist is applied only after rejecting values that browsers or the
 * router can normalize after validation: protocol-relative URLs, backslashes,
 * control characters, and literal or percent-encoded dot segments.
 */

const SAFE_REDIRECT_PREFIXES = [
  '/member-onboarding',
  '/emails',
  '/calendar',
  '/dashboard',
  '/clients',
  '/matters',
  '/activities',
  '/accounting',
  '/expenses',
  '/suppliers',
  '/help',
  '/subscribe',
  '/onboarding',
  '/select-account',
  '/settings',
  '/invoices',
  '/bank-accounts',
  '/referral',
  '/invite/',
  '/connect/authorize',
];

export function isSafeRedirect(value: string | null): value is string {
  if (!value) return false;
  // Must be an absolute in-app path. This rejects relative paths and schemes
  // such as "javascript:" or "https:" before the prefix allowlist runs.
  if (!value.startsWith('/')) return false;
  // Browsers strip ASCII controls such as tab/LF/CR during URL parsing, which
  // can re-form a protocol-relative URL after validation.
  if ([...value].some((ch) => ch.charCodeAt(0) <= 0x1f || ch.charCodeAt(0) === 0x7f)) {
    return false;
  }
  // Browsers treat backslash as a path separator for http(s) URLs, so reject it
  // everywhere rather than trying to reason about normalized forms.
  if (value.includes('\\')) return false;
  // Reject protocol-relative URLs (//evil.com).
  if (/^\/\//.test(value)) return false;

  const path = value.split(/[?#]/, 1)[0];
  // Reject dot segments before the router/browser can normalize an allowlisted
  // prefix into a disallowed route, e.g. /account/../oauth/callback.
  if (/(^|\/)(\.|%2e){1,2}(\/|$)/i.test(path)) return false;

  return path === '/' || SAFE_REDIRECT_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) return value.startsWith(prefix);
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}
